import {
  Box,
  Container,
  Typography,
  Link,
  Grid,
  IconButton,
  Button,
  Divider,
} from '@mui/material';
import {
  Email as EmailIcon,
  Launch as LaunchIcon,
} from '@mui/icons-material';
import { useThemeMode } from '../contexts/ThemeContext';
import { Link as RouterLink } from 'react-router-dom';

const Footer = () => {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';

  return (
    <Box
      component="footer"
      sx={{
        py: 8,
        mt: 'auto',
        background: isDark
          ? 'linear-gradient(180deg, rgba(11, 17, 32, 0.95) 0%, rgba(17, 24, 39, 0.98) 100%)'
          : 'linear-gradient(180deg, rgba(248, 250, 252, 0.95) 0%, rgba(241, 245, 249, 0.98) 100%)',
        backdropFilter: 'blur(20px)',
        borderTop: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: 'linear-gradient(90deg, transparent 0%, rgba(59, 130, 246, 0.3) 50%, transparent 100%)',
        },
      }}
    >
      {/* Subtle background elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          right: '5%',
          width: '200px',
          height: '200px',
          background: `radial-gradient(circle, ${isDark ? 'rgba(30, 64, 175, 0.08)' : 'rgba(30, 64, 175, 0.05)'} 0%, transparent 70%)`,
          borderRadius: '50%',
          animation: 'pulse 4s ease-in-out infinite',
          '@keyframes pulse': {
            '0%, 100%': { transform: 'scale(1)', opacity: 0.3 },
            '50%': { transform: 'scale(1.1)', opacity: 0.5 },
          },
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '20%',
          left: '10%',
          width: '150px',
          height: '150px',
          background: `radial-gradient(circle, ${isDark ? 'rgba(14, 165, 233, 0.08)' : 'rgba(14, 165, 233, 0.05)'} 0%, transparent 70%)`,
          borderRadius: '50%',
          animation: 'float 6s ease-in-out infinite',
          '@keyframes float': {
            '0%, 100%': { transform: 'translateY(0px)', opacity: 0.3 },
            '50%': { transform: 'translateY(-20px)', opacity: 0.5 },
          },
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={6}>
          {/* Company Info */}
          <Grid item xs={12} md={4}>
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h5"
                sx={{
                  background: 'linear-gradient(135deg, #3B82F6 0%, #0EA5E9 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 700,
                  mb: 2,
                }}
              >
                Sebear RWA Platform
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.8,
                  mb: 3,
                }}
              >
                Institutional-grade real-world asset tokenization with compliant blockchain integration.
                Building the future of decentralized finance.
              </Typography>
              <Button
                variant="outlined"
                component={RouterLink}
                to="/submit"
                sx={{
                  borderColor: 'rgba(59, 130, 246, 0.3)',
                  color: '#60A5FA',
                  '&:hover': {
                    borderColor: 'rgba(59, 130, 246, 0.5)',
                    background: 'rgba(59, 130, 246, 0.1)',
                  },
                }}
              >
                Launch Your Project
                <LaunchIcon sx={{ ml: 1, fontSize: 16 }} />
              </Button>
            </Box>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} md={3}>
            <Typography
              variant="h6"
              sx={{
                color: 'text.primary',
                fontWeight: 600,
                mb: 3,
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -8,
                  left: 0,
                  width: '30px',
                  height: '2px',
                  background: 'linear-gradient(90deg, #1E40AF, #3B82F6)',
                  borderRadius: '1px',
                },
              }}
            >
              Quick Links
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[
                { to: '/', label: 'Home' },
                { to: '/submit', label: 'Submit Project' },
                { to: '/dashboard/investor', label: 'Investor Dashboard' },
                { to: '/dashboard/issuer', label: 'Issuer Dashboard' },
              ].map((link) => (
                <Link
                  key={link.to}
                  component={RouterLink}
                  to={link.to}
                  sx={{
                    color: 'text.secondary',
                    textDecoration: 'none',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      color: '#60A5FA',
                      transform: 'translateX(5px)',
                    },
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </Box>
          </Grid>

          {/* Connect Section */}
          <Grid item xs={12} md={5}>
            <Typography
              variant="h6"
              sx={{
                color: 'text.primary',
                fontWeight: 600,
                mb: 3,
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -8,
                  left: 0,
                  width: '30px',
                  height: '2px',
                  background: 'linear-gradient(90deg, #1E40AF, #3B82F6)',
                  borderRadius: '1px',
                },
              }}
            >
              Connect With Us
            </Typography>

            {/* Social Links */}
            <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
              <IconButton
                href="https://x.com/Sebear_tech"
                target="_blank"
                sx={{
                  background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                  border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
                  color: 'text.secondary',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'rgba(59, 130, 246, 0.1)',
                    borderColor: 'rgba(59, 130, 246, 0.3)',
                    color: '#60A5FA',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(30, 64, 175, 0.2)',
                  },
                }}
              >
                <Box
                  component="svg"
                  viewBox="0 0 24 24"
                  sx={{
                    width: 24,
                    height: 24,
                    fill: 'currentColor',
                  }}
                >
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </Box>
              </IconButton>
              <IconButton
                href="mailto:info@sebear.it"
                sx={{
                  background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                  border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
                  color: 'text.secondary',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'rgba(59, 130, 246, 0.1)',
                    borderColor: 'rgba(59, 130, 246, 0.3)',
                    color: '#60A5FA',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(30, 64, 175, 0.2)',
                  },
                }}
              >
                <EmailIcon />
              </IconButton>
            </Box>

            {/* Contact Info */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <EmailIcon sx={{ color: 'text.disabled', fontSize: '1.25rem' }} />
              <Link
                href="mailto:info@sebear.it"
                sx={{
                  color: 'text.secondary',
                  textDecoration: 'none',
                  transition: 'color 0.3s ease',
                  '&:hover': {
                    color: '#60A5FA',
                  },
                }}
              >
                info@sebear.it
              </Link>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                component="svg"
                viewBox="0 0 24 24"
                sx={{
                  width: 20,
                  height: 20,
                  color: 'text.disabled',
                }}
              >
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" fill="currentColor"/>
              </Box>
              <Link
                href="https://x.com/Sebear_tech"
                target="_blank"
                sx={{
                  color: 'text.secondary',
                  textDecoration: 'none',
                  transition: 'color 0.3s ease',
                  '&:hover': {
                    color: '#60A5FA',
                  },
                }}
              >
                @Sebear_tech
              </Link>
            </Box>
          </Grid>
        </Grid>

        {/* Bottom Section */}
        <Divider sx={{ my: 4, borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)' }} />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'text.disabled',
            }}
          >
            {new Date().getFullYear()} Sebear RWA Platform. All rights reserved.
          </Typography>

          <Box sx={{ display: 'flex', gap: 3 }}>
            <Link
              component={RouterLink}
              to="/privacy"
              sx={{
                color: 'text.disabled',
                textDecoration: 'none',
                fontSize: '0.875rem',
                transition: 'color 0.3s ease',
                '&:hover': {
                  color: '#60A5FA',
                },
              }}
            >
              Privacy Policy
            </Link>
            <Link
              component={RouterLink}
              to="/terms"
              sx={{
                color: 'text.disabled',
                textDecoration: 'none',
                fontSize: '0.875rem',
                transition: 'color 0.3s ease',
                '&:hover': {
                  color: '#60A5FA',
                },
              }}
            >
              Terms of Service
            </Link>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
